import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, vShow as _vShow, withDirectives as _withDirectives, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "bulk-actions-wrap" }
const _hoisted_2 = { class: "multi-selected-cards-wrap box-border h-[26px]" }
const _hoisted_3 = { class: "text-sm text-important-text mr-1" }
const _hoisted_4 = { class: "min-w-6 inline-flex items-center justify-center" }

import { computed, ref, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useBrowserLocation, useEventListener } from '@vueuse/core';

// components
import ButtonCustom from '../elements/ButtonCustom.vue';
import ListCardStatusPicker from './ListCardStatusPicker.vue';
import ListDropdown from './ListDropdown.vue';
import SuggestionSpinner from '../elements/SuggestionSpinner.vue';
import DateTimePicker from './DateTimePicker.vue';
import FilterDropdown from './filters/FilterDropdown.vue';
import BoardFilterDropdown from './BoardFilterDropdown.vue';
import TooltipTemplate from '../elements/TooltipTemplate.vue';

// utilities
import useCustomIcon from '../../utilities/composables/useCustomIcon';
import useTranslate from '../../utilities/composables/useTranslate';
import useBoardLists from '../../utilities/composables/useBoardLists';
import useBulkActions from '../../utilities/composables/useBulkActions';
import { globalState } from '../../utilities/globalState';
import { isBoardFullyLoaded } from '../../utilities/isFullyLoaded';
import constants from '../../utilities/constants';
import { EpicTabId } from '../../types/tabs';
import capitalizeFirstLetter from '../../utilities/capitalizeFirstLetter';


export default /*@__PURE__*/_defineComponent({
  __name: 'CardsBulkActions',
  setup(__props) {

const { translate } = useTranslate();
const { getCustomIconStyle, iconPath } = useCustomIcon();

const store = useStore();
const route = useRoute();
const router = useRouter();
const selectedSubMenu = ref<string | null>(null);
const body = ref(document.body);

const listIds = computed(() => store.getters.boardsListsIds[store.getters.currentBoardId] || []);
const { boardLists } = useBoardLists(listIds);
// reactive window location
const location = useBrowserLocation();

const isInEpicBoard = computed(() => {
  const realRoute = router.resolve(location.value.pathname || '');
  return (
    realRoute.name === constants.epicRoute.name &&
    realRoute.params?.selectedTabId === EpicTabId.Cards
  );
});

const isEpicsBoard = computed(
  () => route.name === constants.epicsBoardRoute.name && !isInEpicBoard.value
);

const {
  allCardsAlreadyFavourited,
  allCardsSubscribed,
  bulkMembers,
  bulkSelectedDueDate,
  bulkSelectedStartDate,
  bulkTags,
  bulkTagChange,
  clearMultiSelectedCardIds,
  bulkChangeStatus,
  bulkSetCardDates,
  multiSelectCards,
  bulkMemberChange,
  bulkAddFavourite,
  bulkRemoveFavourite,
  bulkSubscribe,
  bulkMoveCardToBoard,
  bulkMembersSearchValue,
  bulkTagsSearchValue,
  copyCardTitlesToClipboard,
  copyCardTitlesAsLinksToClipboard,
  stopBulkAction,
} = useBulkActions();

const escListener = useEventListener('keydown', (e) => {
  if (e.key === 'Escape' && globalState.bulkActionsInProgress) {
    stopBulkAction();
  }
});

onUnmounted(() => {
  escListener();
});

const bulkActionsIcon = (isVisible: boolean) => {
  return getCustomIconStyle(iconPath(isVisible ? 'chevron-up' : 'chevron-down'));
};

const xTooltipText = computed(() => {
  return globalState.bulkActionsInProgress
    ? capitalizeFirstLetter(translate('stop'))
    : translate('clearSelection');
});

const handleOnShow = () => {
  if (globalState.bulkActionsInProgress) return false;
};

const handleOnHide = () => {
  if (bulkMembersSearchValue.value) bulkMembersSearchValue.value = '';
  if (bulkTagsSearchValue.value) bulkTagsSearchValue.value = '';
  selectedSubMenu.value = null;
};

const bulkActionsDropdownItems = computed(() => {
  return [
    {
      id: allCardsSubscribed.value ? 'unsubscribe' : 'subscribe',
      label: translate(allCardsSubscribed.value ? 'unsubscribe' : 'subscribe'),
      customIconName: iconPath(allCardsSubscribed.value ? 'bell-off' : 'bell'),
      disabled: false,
    },
    {
      id: allCardsAlreadyFavourited.value ? 'unfavourite' : 'favourite',
      label: translate(allCardsAlreadyFavourited.value ? 'unfavourite' : 'favourite'),
      customIconName: iconPath(allCardsAlreadyFavourited.value ? 'unfavourite' : 'star'),
      disabled: false,
    },
    ...(!isInEpicBoard.value
      ? [
          {
            id: 'changeStatus',
            label: translate('changeStatus'),
            customIconName: iconPath('status'),
            disabled: false,
          },
        ]
      : []),
    {
      id: 'assignees',
      label: translate('assignees'),
      customIconName: iconPath('user-plus'),
    },
    {
      id: 'tags',
      label: translate('tags'),
      customIconName: iconPath('tag'),
    },
    {
      id: 'startDate',
      label: translate('startDate'),
      customIconName: iconPath('calendar'),
    },
    {
      id: 'dueDate',
      label: translate('dueDate'),
      customIconName: iconPath('calendar'),
    },
    {
      id: 'copyCardTitles',
      label: translate('copyTitles'),
      customIconName: iconPath('copy'),
    },
    {
      id: 'copyCardTitlesAsLinks',
      label: translate('copyTitlesAsLinks'),
      customIconName: iconPath('link-st'),
    },
  ];
});

const handleChangeStatus = (status: any, hide: () => void) => {
  hide?.();
  bulkChangeStatus(status);
};

const handleMemberSelected = (member: any, hide: () => void) => {
  hide();
  bulkMemberChange(member);
};

const setStartDate = (date: Date, hide: () => void) => {
  hide?.();
  bulkSetCardDates('start_date', date);
};

const setDueDate = (date: Date, hide: () => void) => {
  hide?.();
  bulkSetCardDates('due_date', date);
};

const handleTagClick = (tag: any, hide: () => void) => {
  hide();
  bulkTagChange(tag);
};

const handleMoveCardToBoard = (item: Record<string, any>, hide: () => void) => {
  hide();
  bulkMoveCardToBoard(item.board.id);
};

const handleBulkActionsClick = (item: any, hide: () => void) => {
  switch (item.id) {
    case 'subscribe':
      bulkSubscribe(true);
      hide();
      break;
    case 'unsubscribe':
      bulkSubscribe(false);
      hide();
      break;
    case 'favourite':
      bulkAddFavourite();
      hide();
      break;
    case 'unfavourite':
      bulkRemoveFavourite();
      hide();
      break;
    case 'copyCardTitles':
      copyCardTitlesToClipboard();
      hide();
      break;
    case 'copyCardTitlesAsLinks':
      copyCardTitlesAsLinksToClipboard();
      hide();
      break;
    default:
      selectedSubMenu.value = item.id;
      break;
  }
};

const handleBoardItemHover = (item: any) => {
  const board = store.getters.getFlatBoards[item.id];
  if (!isBoardFullyLoaded(board)) {
    store.dispatch('fetchBoard', { boardId: item.id });
  }
};

const xButtonHandler = () => {
  if (globalState.bulkActionsInProgress) {
    stopBulkAction();
  } else {
    clearMultiSelectedCardIds();
  }
};

const handleBulkMembersQueryChange = ({ value }: { value: string }) => {
  bulkMembersSearchValue.value = value;
};

const handleBulkTagsQueryChange = ({ value }: { value: string }) => {
  bulkTagsSearchValue.value = value;
};

return (_ctx: any,_cache: any) => {
  const _component_v_popover = _resolveComponent("v-popover")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("em", {
        class: "custom-icons custom mr-1 bg-attention-icon ml-1",
        style: _normalizeStyle(_unref(getCustomIconStyle)(_unref(iconPath)('card-icon')))
      }, null, 4),
      _cache[1] || (_cache[1] = _createTextVNode()),
      _createElementVNode("span", _hoisted_3, [
        _createElementVNode("span", _hoisted_4, _toDisplayString(_unref(multiSelectCards).length), 1),
        _createTextVNode(" " + _toDisplayString((_unref(multiSelectCards).length === 1 ? _unref(translate)('card') : _unref(translate)('cards')).toLowerCase()) + " " + _toDisplayString(_unref(translate)('selected').toLowerCase()), 1)
      ]),
      _cache[2] || (_cache[2] = _createTextVNode()),
      (_unref(globalState).bulkActionsInProgress)
        ? (_openBlock(), _createBlock(SuggestionSpinner, {
            key: 0,
            class: "ml-4 mr-3"
          }))
        : _createCommentVNode("", true),
      _cache[3] || (_cache[3] = _createTextVNode()),
      _createVNode(_component_v_popover, { offset: [0, 14] }, {
        content: _withCtx(() => [
          _createVNode(TooltipTemplate, {
            "tooltip-text": xTooltipText.value,
            "tooltip-meta": { icon: { key: 'Esc' } }
          }, null, 8, ["tooltip-text"])
        ]),
        default: _withCtx(() => [
          _createVNode(ButtonCustom, {
            type: 'attention clear-cards-selection',
            "custom-icon-name": 'x',
            "custom-icon-color": '--var(attentionIcon)',
            "custom-icon-size": '16px',
            "button-dimensions": { height: '24px' },
            onClick: xButtonHandler
          }),
          _cache[0] || (_cache[0] = _createTextVNode())
        ]),
        _: 1
      })
    ]),
    _cache[11] || (_cache[11] = _createTextVNode()),
    _cache[12] || (_cache[12] = _createElementVNode("div", { class: "divider-line" }, null, -1)),
    _cache[13] || (_cache[13] = _createTextVNode()),
    (!(isInEpicBoard.value || isEpicsBoard.value))
      ? (_openBlock(), _createBlock(_component_v_popover, {
          key: 0,
          "data-test-move-to-board": "",
          interactive: "",
          tag: "div",
          "content-tag": "div",
          role: 'poper',
          placement: "top-start",
          trigger: 'click',
          "append-to": body.value,
          offset: [0, 14],
          "on-show": handleOnShow
        }, {
          default: _withCtx(({ state }) => [
            _createVNode(ButtonCustom, {
              text: _unref(translate)('moveToBoard'),
              type: 'attention bulk-actions-button mr-2',
              "custom-icon-name": 'arrow-right',
              "custom-icon-size": '16px',
              "custom-icon-color": '--var(attentionIcon)',
              "button-dimensions": { height: '24px' },
              disabled: _unref(globalState).bulkActionsInProgress
            }, {
              rightIcon: _withCtx(() => [
                _createElementVNode("em", {
                  class: "button-icon custom-icons custom ml-1 mr-0",
                  style: _normalizeStyle(bulkActionsIcon(state.isVisible))
                }, null, 4)
              ]),
              _: 2
            }, 1032, ["text", "disabled"])
          ]),
          content: _withCtx(({ state, hide }) => [
            _withDirectives(_createVNode(BoardFilterDropdown, {
              ref: "boardDropdown",
              "selected-board-id": '',
              class: "template-card-board-popover top-7",
              onItemHover: handleBoardItemHover,
              onSelectBoard: ($event: any) => (handleMoveCardToBoard($event, hide)),
              onHidePopup: hide
            }, null, 8, ["onSelectBoard", "onHidePopup"]), [
              [_vShow, state.isVisible]
            ])
          ]),
          _: 1
        }, 8, ["append-to"]))
      : _createCommentVNode("", true),
    _cache[14] || (_cache[14] = _createTextVNode()),
    _createVNode(_component_v_popover, {
      interactive: "",
      tag: "div",
      "content-tag": "div",
      placement: "top-start",
      trigger: "click",
      role: 'poper',
      "append-to": body.value,
      offset: [0, 14],
      "on-show": handleOnShow,
      "on-hide": handleOnHide
    }, {
      default: _withCtx(({ state }) => [
        _createVNode(ButtonCustom, {
          text: _unref(translate)('moreActions'),
          type: 'attention bulk-actions-button',
          "custom-icon-name": 'more-horizontal',
          "custom-icon-size": '16px',
          "custom-icon-color": '--var(attentionIcon)',
          "button-dimensions": { height: '24px' },
          disabled: _unref(globalState).bulkActionsInProgress
        }, {
          rightIcon: _withCtx(() => [
            _createElementVNode("em", {
              class: "button-icon custom-icons custom ml-1 mr-0",
              style: _normalizeStyle(bulkActionsIcon(state.isVisible))
            }, null, 4)
          ]),
          _: 2
        }, 1032, ["text", "disabled"])
      ]),
      content: _withCtx(({ hide, state }) => [
        (state.isVisible && !selectedSubMenu.value)
          ? (_openBlock(), _createBlock(ListDropdown, {
              key: 0,
              ref: "dropdown",
              items: bulkActionsDropdownItems.value,
              onClick: ($event: any) => (handleBulkActionsClick($event, hide)),
              onCloseEscape: hide
            }, null, 8, ["items", "onClick", "onCloseEscape"]))
          : _createCommentVNode("", true),
        _cache[5] || (_cache[5] = _createTextVNode()),
        (state.isVisible && selectedSubMenu.value === 'changeStatus')
          ? (_openBlock(), _createBlock(ListCardStatusPicker, {
              key: 1,
              "custom-statuses-list": _unref(boardLists),
              "show-dropdown-label": false,
              "allow-color-change": false,
              onClick: ($event) => handleChangeStatus($event, hide),
              onClose: hide,
              onEsc: hide
            }, null, 8, ["custom-statuses-list", "onClick", "onClose", "onEsc"]))
          : _createCommentVNode("", true),
        _cache[6] || (_cache[6] = _createTextVNode()),
        (state.isVisible && selectedSubMenu.value === 'assignees')
          ? (_openBlock(), _createBlock(FilterDropdown, {
              key: 2,
              "search-results": _unref(bulkMembers),
              placeholder: `${_unref(translate)('assignees')}...`,
              "hide-filter-popup": hide,
              onItemClick: ($event: any) => (handleMemberSelected($event, hide)),
              onItemRightSideClick: ($event: any) => (handleMemberSelected($event, hide)),
              onQueryChange: handleBulkMembersQueryChange,
              onEscape: hide
            }, null, 8, ["search-results", "placeholder", "hide-filter-popup", "onItemClick", "onItemRightSideClick", "onEscape"]))
          : _createCommentVNode("", true),
        _cache[7] || (_cache[7] = _createTextVNode()),
        (state.isVisible && selectedSubMenu.value === 'startDate')
          ? (_openBlock(), _createBlock(DateTimePicker, {
              key: 3,
              "selected-date": _unref(bulkSelectedStartDate),
              onDueDate: (date) => setStartDate(date, hide),
              onEsc: hide
            }, null, 8, ["selected-date", "onDueDate", "onEsc"]))
          : _createCommentVNode("", true),
        _cache[8] || (_cache[8] = _createTextVNode()),
        (state.isVisible && selectedSubMenu.value === 'dueDate')
          ? (_openBlock(), _createBlock(DateTimePicker, {
              key: 4,
              "selected-date": _unref(bulkSelectedDueDate),
              onDueDate: (date) => setDueDate(date, hide),
              onEsc: hide
            }, null, 8, ["selected-date", "onDueDate", "onEsc"]))
          : _createCommentVNode("", true),
        _cache[9] || (_cache[9] = _createTextVNode()),
        (state.isVisible && selectedSubMenu.value === 'tags')
          ? (_openBlock(), _createBlock(FilterDropdown, {
              key: 5,
              "search-results": _unref(bulkTags),
              placeholder: `${_unref(translate)('tags')}...`,
              "hide-filter-popup": hide,
              onItemClick: ($event: any) => (handleTagClick($event, hide)),
              onItemRightSideClick: ($event: any) => (handleTagClick($event, hide)),
              onQueryChange: handleBulkTagsQueryChange,
              onEscape: hide
            }, null, 8, ["search-results", "placeholder", "hide-filter-popup", "onItemClick", "onItemRightSideClick", "onEscape"]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["append-to"])
  ]))
}
}

})